
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { PERMISSION } from '@/permissions';
import Auth from '@/services/auth/auth';
import Toast from '@/utils/toast';
import LocationSelector from './LocationSelector.vue';
import NavbarGroup from '@/components/navigation/NavbarGroup.vue';
import NavbarItem from '@/components/navigation/NavbarItem.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace: string = 'auth';

@Component<Navbar>({
	components: {
		LocationSelector,
		NavbarGroup,
		NavbarItem,
		TextButton
	}
})
export default class Navbar extends Vue {
	@Action('toggleMobileNav', { namespace: 'ui' }) private toggleMobileNav!: (isOpen: boolean) => void;
	@Action('setDesktopNavExpanded', { namespace: 'ui' }) private setDesktopNavExpanded!: (isExpanded: boolean) => void;
	@Action('buildLocalMenu', { namespace: 'menus' }) private buildLocalMenu!: (restaurantId: string) => Promise<void>;
	@Action('resetDashboardFilters', { namespace: 'dashboard' }) private resetDashboardFilters!: () => Promise<void>;
	@Getter('getRestaurants', { namespace }) private restaurants!: Tenant[];
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('isPosIntegrated', { namespace }) private isPosIntegrated!: boolean;
	@Getter('areSuitesEnabled', { namespace }) private areSuitesEnabled!: boolean;
	@Getter('isCateringRestaurant', { namespace }) private isCateringRestaurant!: boolean;
	@Getter('areCateringEventsEnabled', { namespace }) private areCateringEventsEnabled!: boolean;
	@Getter('isK12Location', { namespace }) private isK12Location!: boolean;
	@Getter('isMarketplaceEnabled', { namespace }) private isMarketplaceEnabled!: boolean;
	@Getter('isOrderManagementEnabled', { namespace }) private isOrderManagementEnabled!: boolean;
	@Getter('getMenus', { namespace: 'menus' }) private menus!: Menu[];
	@Getter('isMobileNavOpen', { namespace: 'ui' }) private isMobileNavOpen!: boolean;
	@Getter('isDesktopNavExpanded', { namespace: 'ui' }) private isDesktopNavExpanded!: boolean;
	@Watch('isMobileNavOpen')
	onMobileNavToggle() {
		// Show drawer based on the mobile nav state
		this.drawer = this.isMobileNavOpen;
	}
	@Watch('isDesktopNavExpanded')
	onDesktopNavExpandedChange(isExpanded: boolean) {
		this.mini = !isExpanded;
		sessionStorage.setItem('miniNav', String(this.mini));
	}
	@Watch('isMobile')
	onMobileChange() {
		// Prevents drawer from being open by default on mobile
		this.drawer = !this.isMobile;
	}

	private drawer: boolean = this.isMobile ? false : true;
	private mini: boolean = true;
	private PERMISSION = PERMISSION;

	private get currentEnv(): string {
		return process.env.NODE_ENV;
	}

	private get menuUrl(): string {
		return `${process.env.VUE_APP_MENU_DOMAIN}/${this.restaurant.slug}`;
	}

	private get navbarImage(): string {
		return this.showMiniDrawer ? require('@/assets/images/app8logo.svg') : require('@/assets/images/app8header.png');
	}

	private get logoutBtnText(): string {
		return window.B2BApp ? this.$t('utils.navbar.btn_back_to_app') : this.$t('utils.navbar.btn_logout');
	}

	private get isMobile(): boolean {
		return this.$vuetify.breakpoint.smAndDown;
	}

	/**
	 * Determines if the mini drawer should be shown.
	 * Mobile should always show the full drawer.
	 *
	 * @return {boolean}
	 */
	private get showMiniDrawer(): boolean {
		return this.mini && !this.isMobile;
	}

	/**
	 * Navbar items based on the user's role and location settings.
	 *
	 * @return {NavbarItemSettings[]}
	 */
	private get navbarItems(): NavbarItemSettings[] {
		return [
			{
				id: 'dashboard',
				name: this.$t('utils.navbar.btn_dashboard'),
				icon: 'mdi-view-dashboard-outline',
				to: '/dashboard',
				hidden: !this.restaurant.dashboard || this.restaurant.k12 || !this.hasPermission(PERMISSION.ROUTE_DASHBOARD),
				onClick: this.handleDashboardReset
			},
			{
				id: 'inquiries',
				name: this.$t('utils.navbar.btn_inquiries'),
				icon: 'mdi-message-badge-outline',
				to: '/inquiries',
				hidden: !this.areCateringEventsEnabled || !this.hasPermission(PERMISSION.ROUTE_INQUIRIES)
			},
			{
				id: 'order-management',
				name: this.$t('utils.navbar.btn_order_management'),
				icon: 'mdi-receipt-clock-outline',
				to: '/order-management',
				hidden: !(this.isMarketplaceEnabled || this.isOrderManagementEnabled) || !this.hasPermission(PERMISSION.ROUTE_ORDER_MANAGEMENT)
			},
			{
				id: 'orders-report',
				name: this.$t('utils.navbar.btn_orders_report'),
				icon: 'mdi-clipboard-check-outline',
				to: '/orders-report',
				hidden: !(this.isCateringRestaurant || this.isK12Location)
			},
			{
				id: 'settings',
				name: this.$t('utils.navbar.btn_settings'),
				icon: 'mdi-cog',
				to: '/settings',
				hidden: !this.hasPermission(PERMISSION.ROUTE_SETTINGS)
			},
			{
				id: 'menus-group',
				name: this.$t('utils.navbar.btn_menu'),
				children: [
					{
						id: 'menus',
						name: this.$t('utils.navbar.btn_menu'),
						icon: 'mdi-silverware-fork-knife',
						to: '/menus'
					},
					{
						id: 'availability-setting',
						name: this.$t('utils.navbar.btn_availability'),
						icon: 'mdi-calendar-month-outline',
						to: '/availabilities',
						hidden: !this.menus?.length
					}
				]
			},
			{
				id: 'events-group',
				name: this.$t('utils.navbar.btn_event'),
				hidden: !this.areSuitesEnabled || !this.hasPermission(PERMISSION.ROUTE_EVENTS),
				children: [
					{
						id: 'events',
						name: this.$t('utils.navbar.btn_event'),
						icon: 'mdi-stadium-outline',
						to: '/events'
					},
					{
						id: 'event-setting',
						name: this.$t('utils.navbar.btn_event_settings'),
						icon: 'mdi-clipboard-check-outline',
						to: '/event-settings'
					}
				]
			},
			{
				id: 'rally-intelligence-group',
				name: this.$t('utils.navbar.btn_rally_intelligence'),
				hidden: !this.restaurant.rally_intelligence || !this.hasPermission(PERMISSION.ADMIN_ONLY),
				children: [
					{
						id: 'rally-intelligence',
						name: this.$t('utils.navbar.btn_rally_intelligence'),
						icon: 'mdi-head-snowflake-outline',
						to: '/rally-intelligence'
					}
				]
			}
		];
	}

	private created() {
		// Set initial nav state from session
		const miniNav = sessionStorage.getItem('miniNav');
		this.mini = miniNav === 'true' && !this.isMobile || false;
		this.setDesktopNavExpanded(!this.mini);
	}

	/**
	 * Log the user out
	 *
	 * @return {void}
	 */
	private logout(): void {
		window.B2BApp ? window.B2BApp.backToApp() : Auth.logout();
	}

	/**
	 * Resize event for the drawer, we want to move the dynamic main btn that is fixed
	 * so it stays centered
	 *
	 * @param {boolean} expand
	 * @return {void}
	 */
	private resize(expand: boolean): void {
		this.mini = !expand;
		sessionStorage.setItem('miniNav', String(this.mini));
		this.setDesktopNavExpanded(expand);

		setTimeout(() => {
			const dynamicBtn = document.getElementById('main-dynamic-btn');
			if(dynamicBtn) {
				const drawerWidth = document.getElementById('nav-drawer')!.offsetWidth;
				document.documentElement.style.setProperty('--dynamic-btn-left-value', `${drawerWidth}px`);
			}
		}, 200);
	}

	private async handleDashboardReset(): Promise<void> {
		if (this.$route.path === '/dashboard') {
			try {
				await this.resetDashboardFilters();
			}
			catch (errorMessage) {
				Toast.error(this.$t('orders.dashboard.error_fetching_data', { errorMessage }));
			}
		}
	}
}
